import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { finalize, take, takeUntil } from "rxjs/operators";
import { Perfil } from "src/app/models/Perfil/Perfil";
import { Permiso } from "src/app/models/Perfil/Permiso";
import { PerfilService } from "src/app/services/perfil.service";
import { PermissionService } from "src/app/services/permission.service";
import Swal from "sweetalert2";
import { PERMISSION_CATEGORIES, PERMISSION_PRESENTATION, SUPPORT_PERMISSION_CODES, PermissionCategoryDefinition } from "./permission-catalog";

export interface PermissionItem {
	permission: Permiso;
	title: string;
	description: string;
	searchText: string;
}

export interface PermissionCategory extends PermissionCategoryDefinition {
	order: number;
	items: PermissionItem[];
	visibleItems: PermissionItem[];
	expanded: boolean;
}

@Component({
	selector: "app-permission-list",
	templateUrl: "./permission-list.component.html",
	styleUrls: ["./permission-list.component.scss"],
})
export class PermissionListComponent implements OnInit, OnDestroy {
	public currentProfile: Perfil;
	public categories: PermissionCategory[] = [];
	public visibleCategories: PermissionCategory[] = [];
	public visibleColumns: PermissionCategory[][] = [[], []];
	public loading = false;
	public loadError = "";
	public returnURL = "/configuracion/perfiles";
	public searchTerm = "";
	private savedAssignments = "";
	private profileId: string;
	private profileName = "";
	private readonly destroyed$ = new Subject<void>();

	constructor(
		public permissions: PermissionService,
		private route: ActivatedRoute,
		private perfilService: PerfilService,
		private ref: ChangeDetectorRef,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.route.queryParamMap.pipe(take(1), takeUntil(this.destroyed$)).subscribe((params) => {
			this.profileId = params.get("perfilId");
			this.profileName = params.get("perfilNombre") || "";
			let legacyProfile: Perfil | undefined = undefined;
			try {
				legacyProfile = JSON.parse(window.history.state?.rowProfile);
			} catch {}
			if (!this.profileName) this.profileName = legacyProfile?.nombre || "";
			// Preserve links opened before the identifier was included in the URL.
			if (!this.profileId) {
				this.profileId = legacyProfile?.perfilId;
				if (this.profileId) {
					this.router.navigate([], { relativeTo: this.route, queryParams: { perfilId: this.profileId, perfilNombre: this.profileName || null }, queryParamsHandling: "merge", replaceUrl: true });
				}
			}
			this.reloadProfile();
		});
	}

	reloadProfile(): void {
		if (this.loading) {
			return;
		}
		if (!this.profileId) {
			this.loadError = "Selecciona un perfil desde Perfiles para editar sus permisos.";
			return;
		}
		this.loadError = "";
		this.loading = true;
		this.perfilService
			.getPermissions("profile", this.profileId)
			.pipe(
				takeUntil(this.destroyed$),
				finalize(() => {
					this.loading = false;
					this.ref.markForCheck();
				}),
			)
			.subscribe(
				(response) => this.loadProfile({ perfilId: this.profileId, nombre: this.profileName, permisos: response?.data } as Perfil),
				() => {
					this.loadError = "No se pudieron obtener los permisos del perfil. Intenta nuevamente.";
				},
			);
	}

	get canRetryLoad(): boolean {
		return !!this.profileId;
	}

	loadProfile(profile: Perfil): void {
		this.loadError = "";
		this.currentProfile = undefined;
		this.categories = [];
		this.visibleCategories = [];
		this.visibleColumns = [[], []];
		this.searchTerm = "";
		try {
			if (!profile?.perfilId || !Array.isArray(profile.permisos)) {
				throw new Error("Missing profile");
			}
			this.currentProfile = profile;
			this.categories = PERMISSION_CATEGORIES.map((category, order) => ({ ...category, order, items: [], visibleItems: [], expanded: false }));
			profile.permisos.forEach((permission) => {
				const code = (permission.codigo || "").trim();
				// Hidden/inactive entries remain in currentProfile: the API replaces every assignment on save.
				if (permission.activo === false || SUPPORT_PERMISSION_CODES.has(code)) {
					return;
				}
				const presentation = PERMISSION_PRESENTATION[code];
				const category = this.categories.find((item) => item.id === (presentation?.categoryId || "otros"));
				const title = presentation?.title || permission.nombre?.trim() || "Permiso sin nombre";
				const description = presentation?.description || "Habilita esta función para las personas que tengan el perfil.";
				category.items.push({ permission, title, description, searchText: this.normalize(`${title} ${description} ${permission.nombre} ${code}`) });
			});
			this.categories = this.categories.filter((category) => category.items.length > 0);
			this.savedAssignments = this.assignmentSnapshot();
			this.filterCategories();
		} catch {
			this.currentProfile = undefined;
			this.categories = [];
			this.visibleCategories = [];
			this.visibleColumns = [[], []];
			this.loadError = "No se pudieron obtener los datos del perfil. Intenta nuevamente.";
		}
	}

	get profileDisplayName(): string {
		const name = this.currentProfile?.nombre?.trim();
		if (!name) return "seleccionado";
		return name
			.replace(/([a-záéíóúñ])([A-ZÁÉÍÓÚÑ])/g, "$1 $2")
			.replace(/[_-]+/g, " ")
			.replace(/\s+/g, " ");
	}

	get profileDescription(): string {
		const description = (this.currentProfile as Perfil & { descripcion?: string })?.descripcion?.trim();
		if (description) return description;
		const profileReference = this.currentProfile?.nombre?.trim() ? `el perfil ${this.profileDisplayName}` : "este perfil";
		return `Define qué puede consultar y administrar ${profileReference} dentro de SimpleFactura.`;
	}

	get totalCount(): number {
		return this.categories.reduce((total, category) => total + category.items.length, 0);
	}
	get assignedCount(): number {
		return this.categories.reduce((total, category) => total + this.countAssigned(category), 0);
	}
	get percentage(): number {
		return this.totalCount ? Math.round((this.assignedCount * 100) / this.totalCount) : 0;
	}
	get allAssigned(): boolean {
		return this.totalCount > 0 && this.assignedCount === this.totalCount;
	}
	get allExpanded(): boolean {
		return this.visibleCategories.length > 0 && this.visibleCategories.every((category) => category.expanded);
	}
	get hasChanges(): boolean {
		return !!this.currentProfile && this.assignmentSnapshot() !== this.savedAssignments;
	}
	get visibleCount(): number {
		return this.visibleCategories.reduce((total, category) => total + category.visibleItems.length, 0);
	}

	countAssigned(category: PermissionCategory): number {
		return category.items.filter((item) => item.permission.asignado).length;
	}

	filterCategories(): void {
		const query = this.normalize(this.searchTerm.trim());
		this.visibleCategories = this.categories.filter((category) => {
			const matchesCategory = this.normalize(`${category.title} ${category.description}`).includes(query);
			category.visibleItems = category.items.filter((item) => !query || matchesCategory || item.searchText.includes(query));
			if (query && category.visibleItems.length) {
				category.expanded = true;
			}
			return category.visibleItems.length > 0;
		});
		this.visibleColumns = [this.visibleCategories.filter((_, index) => index % 2 === 0), this.visibleCategories.filter((_, index) => index % 2 === 1)];
	}

	setAll(assigned: boolean): void {
		this.categories.forEach((category) => this.setCategory(category, assigned));
	}

	setCategory(category: PermissionCategory, assigned: boolean): void {
		category.items.forEach((item) => (item.permission.asignado = assigned));
	}

	toggleExpanded(): void {
		const expand = !this.allExpanded;
		this.visibleCategories.forEach((category) => (category.expanded = expand));
	}

	sendPermissionsList(): void {
		if (!this.currentProfile || this.loading || !this.hasChanges) {
			return;
		}
		// Send the full catalog, not the filtered cards; changing the view must never revoke hidden permissions.
		const request = { ...this.currentProfile, permisos: this.currentProfile.permisos.map((permission) => ({ ...permission })) };
		this.loading = true;
		this.perfilService
			.updatePermissionsProfile(request)
			.pipe(
				takeUntil(this.destroyed$),
				finalize(() => {
					this.loading = false;
					this.ref.markForCheck();
				}),
			)
			.subscribe(
				(response) => {
					if (response.data !== true) {
						this.showSaveError();
						return;
					}
					this.savedAssignments = this.assignmentSnapshot();
					Swal.fire({ icon: "success", title: "Permisos guardados", text: "Se actualizaron los permisos del perfil." });
				},
				() => this.showSaveError(),
			);
	}

	trackCategory(index: number, category: PermissionCategory): string {
		return category.id;
	}
	trackPermission(index: number, item: PermissionItem): string {
		return item.permission.permisoId;
	}

	private assignmentSnapshot(): string {
		return JSON.stringify((this.currentProfile?.permisos || []).map((permission) => [permission.permisoId, !!permission.asignado]));
	}

	private normalize(value: string): string {
		return (value || "")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLocaleLowerCase();
	}

	private showSaveError(): void {
		Swal.fire({ icon: "error", title: "No se pudieron guardar los cambios", text: "Tus cambios se mantienen en pantalla. Intenta guardar nuevamente." });
	}

	ngOnDestroy(): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}
}
